/**
 * ระบบบัญชีฝั่ง client
 *
 * คุยกับ `/api/account/*` ของเซิร์ฟเวอร์เกม แล้วเก็บโทเคนไว้ในเครื่อง
 * **ไม่มีการตัดสินใจเรื่องสิทธิ์ที่นี่** server เป็นคนตรวจโทเคนตอนเข้าห้องเสมอ
 * ของพวกนี้มีไว้ให้หน้าจอรู้ว่าจะแสดงอะไร ไม่ใช่ด่านกันคนเข้า
 *
 * โทเคนเก็บใน `localStorage` ต้องครอบ try/catch เพราะโหมดส่วนตัวของบางเบราว์เซอร์
 * เข้าถึงไม่ได้ ถ้าอ่านเขียนไม่ได้ก็แค่ต้องล็อกอินใหม่ทุกครั้ง ไม่ควรทำให้เกมเปิดไม่ขึ้น
 */

const TOKEN_KEY = "wickhaven.token";

export interface AccountResult {
  ok: boolean;
  /** ข้อความไทยที่แสดงได้ตรง ๆ มีเมื่อ ok เป็น false */
  text?: string;
  username?: string;
}

/**
 * อ่านโทเคน — ดูของแท็บนี้ก่อน แล้วค่อยถอยไปดูของทั้งเบราว์เซอร์
 *
 * `localStorage` ใช้ร่วมกันทุกแท็บ ถ้าอ่านจากที่นั่นอย่างเดียว
 * เปิดแท็บที่สองจะกลายเป็นบัญชีเดียวกับแท็บแรกเสมอ แล้วโดนปฏิเสธเพราะเข้าซ้ำ
 * (ทดสอบสองบัญชีพร้อมกันบนเครื่องเดียวไม่ได้เลย ซึ่งเป็นสิ่งที่ต้องทำบ่อยตอนพัฒนา)
 *
 * `sessionStorage` แยกตามแท็บและอยู่ข้ามการรีเฟรช จึงเก็บ "บัญชีของแท็บนี้" ไว้ที่นั่น
 * ส่วน `localStorage` เก็บ "บัญชีล่าสุด" ไว้ให้แท็บใหม่หยิบไปใช้
 */
export function loadToken(): string {
  try {
    return sessionStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
}

export function saveToken(token: string): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // เก็บไม่ได้ก็เล่นต่อได้ แค่ต้องล็อกอินใหม่รอบหน้า
  }
}

export function clearToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ไม่เป็นไร
  }
}

/** ที่อยู่ HTTP ของเซิร์ฟเวอร์เกม แปลงจาก URL ของ WebSocket ที่ใช้อยู่ */
/**
 * ที่อยู่เซิร์ฟเวอร์เกมจากค่าตอน build (`VITE_SERVER_URL`)
 *
 * - ไม่ตั้ง → เครื่องเดียวกับหน้าเว็บ พอร์ต 2567 (dev)
 * - ws:// หรือ wss:// → ใช้ตามนั้น
 * - ชื่อโฮสต์เปล่า เช่น `wickhaven-server.onrender.com` → wss:// (โฮสต์จริงเป็น https เสมอ)
 */
export function resolveServerUrl(env: string | undefined, hostname: string): string {
  const v = (env ?? "").trim().replace(/\/+$/, "");
  if (!v) return `ws://${hostname}:2567`;
  if (/^wss?:\/\//.test(v)) return v;
  if (/^https?:\/\//.test(v)) return v.replace(/^http/, "ws");
  return `wss://${v}`;
}

export function httpBase(serverUrl: string): string {
  return serverUrl.replace(/^ws/, "http");
}

/** ข้อความสำรองเมื่อเซิร์ฟเวอร์ไม่ได้ส่งข้อความมา (เช่นต่อไม่ติดเลย) */
const FALLBACK = "ติดต่อเซิร์ฟเวอร์ไม่ได้";

async function post(base: string, path: string, body: unknown): Promise<{ status: number; json: any; error?: string }> {
  try {
    const res = await fetch(`${base}/api/account${path}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    return { status: res.status, json };
  } catch (err: unknown) {
    const msg = (err as Error)?.message ?? "Network Error";
    return { status: 0, json: {}, error: msg };
  }
}

export async function register(
  base: string,
  username: string,
  password: string,
): Promise<AccountResult> {
  const u = username.trim();
  if (!u) return { ok: false, text: "⚠️ กรุณากรอกชื่อบัญชี" };
  if (!/^[a-zA-Z0-9_]{3,16}$/.test(u)) {
    return { ok: false, text: "⚠️ ชื่อบัญชีต้องเป็นภาษาอังกฤษ/ตัวเลข/ขีดล่าง 3-16 ตัวอักษร" };
  }
  if (!password) return { ok: false, text: "⚠️ กรุณากรอกรหัสผ่าน" };
  if (password.length < 8) return { ok: false, text: "⚠️ รหัสผ่านต้องมีความยาวอย่างน้อย 8 ตัวอักษร" };

  const r = await post(base, "/register", { username: u, password });
  if (r.status === 0) {
    return { ok: false, text: `📡 ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ (${base}) กรุณาตรวจสอบอินเทอร์เน็ตหรือรอเซิร์ฟเวอร์ตื่นสักครู่` };
  }
  if (r.status === 503) {
    return { ok: false, text: r.json?.text ?? "⚠️ ระบบฐานข้อมูลยังไม่พร้อม กรุณารอประมาณ 30 วินาทีแล้วลองใหม่อีกครั้ง" };
  }
  if (r.status === 409 || r.json?.reason === "duplicate") {
    return { ok: false, text: "⚠️ ชื่อบัญชีนี้มีผู้ใช้งานแล้ว กรุณาเลือกชื่ออื่น" };
  }
  if (!r.json?.ok) {
    return { ok: false, text: r.json?.text ?? `เกิดข้อผิดพลาด (${r.status}): ${r.error ?? "ไม่สำเร็จ"}` };
  }
  saveToken(r.json.token);
  return { ok: true, username: r.json.username };
}

export async function login(
  base: string,
  username: string,
  password: string,
): Promise<AccountResult> {
  const u = username.trim();
  if (!u) return { ok: false, text: "⚠️ กรุณากรอกชื่อบัญชี" };
  if (!password) return { ok: false, text: "⚠️ กรุณากรอกรหัสผ่าน" };

  const r = await post(base, "/login", { username: u, password });
  if (r.status === 0) {
    return { ok: false, text: `📡 ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ (${base}) กรุณาตรวจสอบอินเทอร์เน็ตหรือรอเซิร์ฟเวอร์ตื่นสักครู่` };
  }
  if (r.status === 503) {
    return { ok: false, text: r.json?.text ?? "⚠️ เซิร์ฟเวอร์ฐานข้อมูลยังไม่พร้อม กรุณารอประมาณ 30 วินาทีแล้วกดใหม่" };
  }
  if (r.status === 401) {
    return { ok: false, text: "❌ ชื่อบัญชีหรือรหัสผ่านไม่ถูกต้อง (หากยังไม่มีบัญชีให้กด 'สมัครบัญชีใหม่')" };
  }
  if (r.status === 429) {
    return { ok: false, text: "⏳ ลองผิดบ่อยเกินไป กรุณารอสักครู่แล้วลองใหม่" };
  }
  if (!r.json?.ok) {
    return { ok: false, text: r.json?.text ?? `เข้าสู่ระบบไม่สำเร็จ (${r.status})` };
  }
  saveToken(r.json.token);
  return { ok: true, username: r.json.username };
}

/** โทเคนที่เก็บไว้ยังใช้ได้ไหม คืนชื่อบัญชีถ้าใช้ได้ */
export async function whoAmI(base: string, token: string): Promise<string> {
  if (!token) return "";
  try {
    const res = await fetch(`${base}/api/account/me`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) return "";
    const json = (await res.json()) as { username?: string };
    return json.username ?? "";
  } catch {
    return "";
  }
}

export interface CharacterInfo {
  id: string;
  /** ช่องในหน้าเลือกตัวละคร 0 ถึง max-1 */
  slot: number;
  name: string;
  cls: string;
  /** เพศ มีผลกับหน้าตาอย่างเดียว */
  gender: string;
  /** หน้าตาที่เลือกเอง JSON (shared/looks.ts) server รุ่นเก่าไม่ส่ง = ค่าเริ่มต้น */
  look?: string;
  baseLevel: number;
  jobLevel: number;
  gold: number;
  stats: Record<string, number>;
  /** โซนที่ตัวละครอยู่ตอนออกเกมครั้งก่อน (server รุ่นเก่าไม่ส่งมา = เมือง) */
  zone?: string;
}

export interface CharacterList {
  max: number;
  characters: CharacterInfo[];
}

function auth(token: string): Record<string, string> {
  return { authorization: `Bearer ${token}` };
}

/**
 * ตัวละครทั้งหมดของบัญชี พร้อมจำนวนช่องสูงสุด
 * ต้องถามก่อนเข้าเกมเสมอ เพราะหน้าเลือกตัวละครต้องรู้ว่าใครอยู่ช่องไหน
 */
export async function listCharacters(base: string, token: string): Promise<CharacterList> {
  if (!token) return { max: 0, characters: [] };
  try {
    const res = await fetch(`${base}/api/account/characters`, { headers: auth(token) });
    if (!res.ok) return { max: 0, characters: [] };
    const json = (await res.json()) as Partial<CharacterList>;
    return { max: json.max ?? 0, characters: json.characters ?? [] };
  } catch {
    return { max: 0, characters: [] };
  }
}

export interface CreateResult extends AccountResult {
  character?: CharacterInfo;
}

/** สร้างตัวละครลงช่องที่เลือก */
export async function createCharacter(
  base: string,
  token: string,
  body: { name: string; cls: string; gender: string; slot: number; look?: string },
): Promise<CreateResult> {
  try {
    const res = await fetch(`${base}/api/account/characters`, {
      method: "POST",
      headers: { "content-type": "application/json", ...auth(token) },
      body: JSON.stringify(body),
    });
    const json = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      text?: string;
      character?: CharacterInfo;
    };
    if (!res.ok || !json.ok) {
      if (res.status === 503) {
        return { ok: false, text: "⚠️ ฐานข้อมูลกำลังเริ่มระบบหรือออฟไลน์ ไม่สามารถบันทึกตัวละครได้" };
      }
      return { ok: false, text: json.text ?? `สร้างตัวละครไม่สำเร็จ (HTTP ${res.status})` };
    }
    return { ok: true, character: json.character! };
  } catch {
    return { ok: false, text: "❌ ไม่สามารถติดต่อเซิร์ฟเวอร์ได้ กรุณาตรวจสอบสัญญาณอินเทอร์เน็ต" };
  }
}

/** ลบตัวละครตัวหนึ่ง — ลบแล้วไม่มีทางกู้คืน */
export async function deleteCharacter(
  base: string,
  token: string,
  id: string,
): Promise<AccountResult> {
  try {
    const res = await fetch(`${base}/api/account/characters/delete`, {
      method: "POST",
      headers: { "content-type": "application/json", ...auth(token) },
      body: JSON.stringify({ id }),
    });
    const json = (await res.json().catch(() => ({}))) as { ok?: boolean; text?: string };
    return json.ok ? { ok: true } : { ok: false, text: json.text ?? FALLBACK };
  } catch {
    return { ok: false, text: FALLBACK };
  }
}

/** ระบบบัญชีพร้อมใช้งานไหม (ฐานข้อมูลเปิดอยู่หรือเปล่า) */
export async function accountsAvailable(base: string): Promise<boolean> {
  try {
    const res = await fetch(`${base}/health`);
    const json = (await res.json()) as { db?: boolean };
    return json.db === true;
  } catch {
    return false;
  }
}
